import React, { useEffect, useState } from 'react';
import { Container, Typography, Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { fetchCuisines } from '../../store/slices/cuisineSlice';
import { fetchRestaurants } from '../../store/slices/restaurantSlice';
import { Cuisine, Restaurant, Dish } from '../../types';
import Carousel from '../../components/Carousel';
import RestaurantCarouselCard from '../../components/RestaurantCarouselCard';
import DishCarouselCard from '../../components/DishCarouselCard';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const Home: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { items: cuisines } = useSelector((state: RootState) => state.cuisines);
  const { items: restaurants } = useSelector((state: RootState) => state.restaurants);
  const [randomCuisine, setRandomCuisine] = useState<Cuisine | null>(null);
  const [cuisineRestaurants, setCuisineRestaurants] = useState<Restaurant[]>([]);
  const [votedDishes1, setVotedDishes1] = useState<Dish[]>([]);
  const [votedDishes2, setVotedDishes2] = useState<Dish[]>([]);
  const [favoriteRestaurants, setFavoriteRestaurants] = useState<Restaurant[]>([]);
  const { token } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const [newRestaurants, setNewRestaurants] = useState<Restaurant[]>([]);
  const [recentlyReviewedDishes, setRecentlyReviewedDishes] = useState<Dish[]>([]);

  useEffect(() => {
    dispatch(fetchCuisines());
    dispatch(fetchRestaurants());
  }, [dispatch]);

  useEffect(() => {
    const fetchRandomCuisineRestaurants = async () => {
      if (cuisines.length > 0) {
        const randomIndex = Math.floor(Math.random() * cuisines.length);
        const selectedCuisine = cuisines[randomIndex];
        setRandomCuisine(selectedCuisine);

        try {
          const response = await axios.get(`/api/restaurants/?cuisine_id=${selectedCuisine.id}`);
          setCuisineRestaurants(response.data.results);
        } catch (error) {
          console.error('Error fetching cuisine restaurants:', error);
        }
      }
    };

    const fetchVotedDishes = async () => {
      try {
        const response = await axios.get('/api/dishes/voted/');
        const dishes = response.data.results;
        
        // Shuffle the dishes array to get random order
        const shuffledDishes = [...dishes].sort(() => Math.random() - 0.5);
        
        // Split into two unique sets
        const uniqueDishes1 = new Set<number>();
        const uniqueDishes2 = new Set<number>();
        
        const dishes1: Dish[] = [];
        const dishes2: Dish[] = [];
        
        shuffledDishes.forEach(dish => {
          if (dishes1.length < 10 && !uniqueDishes1.has(dish.id)) {
            dishes1.push(dish);
            uniqueDishes1.add(dish.id);
          } else if (dishes2.length < 10 && !uniqueDishes2.has(dish.id)) {
            dishes2.push(dish);
            uniqueDishes2.add(dish.id);
          }
        });
        
        setVotedDishes1(dishes1);
        setVotedDishes2(dishes2);
      } catch (error) {
        console.error('Error fetching voted dishes:', error);
      }
    };

    const fetchFavoriteRestaurants = async () => {
      if (token) {
        try {
          const response = await axios.get('/api/auth/favorites/', {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const favorites = response.data.results.map((favorite: { restaurant: Restaurant }) => favorite.restaurant);
          // Remove duplicates based on restaurant ID
          const uniqueFavorites = Array.from(
            new Map(favorites.map((item: Restaurant) => [item.id, item])).values()
          ) as Restaurant[];
          setFavoriteRestaurants(uniqueFavorites);
        } catch (error) {
          console.error('Error fetching favorite restaurants:', error);
        }
      }
    };

    const fetchNewRestaurants = async () => {
      try {
        const response = await axios.get('/api/restaurants/?ordering=-created_at');
        setNewRestaurants(response.data.results || response.data);
      } catch (error) {
        console.error('Error fetching new restaurants:', error);
      }
    };

    const fetchRecentlyReviewedDishes = async () => {
      try {
        const response = await axios.get('/api/dishes/recently_reviewed/');
        setRecentlyReviewedDishes(response.data.results || response.data);
      } catch (error) {
        console.error('Error fetching recently reviewed dishes:', error);
      }
    };

    fetchRandomCuisineRestaurants();
    fetchVotedDishes();
    fetchFavoriteRestaurants();
    fetchNewRestaurants();
    fetchRecentlyReviewedDishes();
  }, [cuisines, token]);

  const handleRestaurantClick = (restaurant: Restaurant) => {
    navigate(`/restaurant/${restaurant.id}`);
  };

  const handleDishClick = (dish: Dish) => {
    navigate(`/dish/${dish.id}`);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Welcome to Atlas.Rest
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Discover amazing restaurants and dishes in your area. From popular cuisines to top-rated dishes, 
          we've got something for everyone.
        </Typography>
      </Box>

      {randomCuisine && cuisineRestaurants.length > 0 && (
        <Carousel title={`Discover ${randomCuisine.name} Cuisine - Popular Restaurants`}>
          {cuisineRestaurants.map((restaurant: Restaurant) => (
            <RestaurantCarouselCard
              key={restaurant.id}
              restaurant={restaurant}
              onClick={handleRestaurantClick}
            />
          ))}
        </Carousel>
      )}

      {votedDishes1.length > 0 && (
        <Carousel title="Top Rated Dishes - Most Loved by Foodies">
          {votedDishes1.map((dish: Dish) => (
            <DishCarouselCard
              key={dish.id}
              dish={dish}
              onClick={handleDishClick}
            />
          ))}
        </Carousel>
      )}

      {votedDishes2.length > 0 && (
        <Carousel title="Trending Dishes - What's Hot Right Now">
          {votedDishes2.map((dish: Dish) => (
            <DishCarouselCard
              key={dish.id}
              dish={dish}
              onClick={handleDishClick}
            />
          ))}
        </Carousel>
      )}

      {token && favoriteRestaurants.length > 0 && (
        <Carousel title="Your Favorites - Restaurants You Love">
          {favoriteRestaurants.map((restaurant: Restaurant) => (
            <RestaurantCarouselCard
              key={restaurant.id}
              restaurant={restaurant}
              onClick={handleRestaurantClick}
            />
          ))}
        </Carousel>
      )}

      {newRestaurants.length > 0 && (
        <Carousel title="New Restaurants - Recently Added">
          {newRestaurants.map((restaurant: Restaurant) => (
            <RestaurantCarouselCard
              key={restaurant.id}
              restaurant={restaurant}
              onClick={handleRestaurantClick}
            />
          ))}
        </Carousel>
      )}

      {recentlyReviewedDishes.length > 0 && (
        <Carousel title="Recently Reviewed Dishes - Fresh Feedback">
          {recentlyReviewedDishes.map((dish: Dish) => (
            <DishCarouselCard
              key={dish.id}
              dish={dish}
              onClick={handleDishClick}
            />
          ))}
        </Carousel>
      )}
    </Container>
  );
};

export default Home; 